/**
 * Step 6 — Verification.
 *
 * Asks the main process to validate the database schema, required master data,
 * cached configuration, and offline readiness, and presents the result as a
 * checklist. Continue is enabled only when every check passes.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import sx from '../setupStyles';
import { setupApi } from '../desktopApi';

const LABELS = {
  database: 'Database Ready',
  masterData: 'Master Data Downloaded',
  configuration: 'Configuration Completed',
  offlineMode: 'Offline Mode Enabled',
};

export default function Verify({ goBack, goNext, patchCtx }) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await setupApi.verify();
      setResult(res);
      patchCtx({ verifyResult: res });
    } catch (err) {
      setError(err && err.message ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  }, [patchCtx]);

  useEffect(() => {
    run();
  }, [run]);

  const checks = (result && result.checks) || {};

  return (
    <>
      <Box sx={sx.body}>
        <Stack spacing={1} sx={{ flex: 1, justifyContent: 'center' }}>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <CircularProgress size={18} />
              <span>Verifying…</span>
            </Stack>
          ) : (
            <>
              {error && <Alert severity="error">{error}</Alert>}
              <List>
                {Object.keys(LABELS).map((key) => (
                  <ListItem key={key}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {checks[key] ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={LABELS[key]} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Stack>
      </Box>
      <Box sx={sx.footer}>
        <Button color="inherit" onClick={goBack}>
          Back
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={run} disabled={loading}>
            Re-check
          </Button>
          <Button variant="contained" onClick={goNext} disabled={loading || !(result && result.ok)}>
            Continue
          </Button>
        </Stack>
      </Box>
    </>
  );
}
